import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { supportApi } from '@/services/api'
import { useAuth } from '@/hooks/useAuth'
import Button from '@/components/ui/Button'
import Badge from '@/components/ui/Badge'
import Loading from '@/components/ui/Loading'
import Alert from '@/components/ui/Alert'
import Modal from '@/components/ui/Modal'
import ResponsiveTable from '@/components/ui/ResponsiveTable'
import SupportTicketImageUpload from '@/components/support/SupportTicketImageUpload'
import PageNotification from '@/components/ui/PageNotification'
import { formatDate } from '@/utils/format'
import { Plus, MessageCircle, CheckCircle, Info } from 'lucide-react'
import type { SupportTicket } from '@/types'

export default function Support() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [selectedTicketId, setSelectedTicketId] = useState<number | null>(null)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [formData, setFormData] = useState({
    category: 'general',
    subject: '',
    message: '',
  })
  const [replyMessage, setReplyMessage] = useState('')

  const isAdmin = user?.user_type === 'super_admin' || user?.user_type === 'admin'

  // Get all tickets (list view)
  const { data, isLoading } = useQuery({
    queryKey: ['support-tickets'],
    queryFn: () => supportApi.list(),
  })

  // Get single ticket with messages
  const { data: ticketData, isLoading: isLoadingTicket } = useQuery({
    queryKey: ['support-ticket', selectedTicketId],
    queryFn: () => supportApi.get(selectedTicketId!),
    enabled: !!selectedTicketId,
  })

  const createMutation = useMutation({
    mutationFn: (data: any) => supportApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['support-tickets'] })
      setShowCreateModal(false)
      setFormData({ category: 'general', subject: '', message: '' })
      setSuccess('Support ticket created successfully!')
      setTimeout(() => setSuccess(''), 3000)
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to create ticket')
    },
  })

  const replyMutation = useMutation({
    mutationFn: ({ id, message }: { id: number; message: string }) =>
      supportApi.reply(id, message),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['support-tickets'] })
      queryClient.invalidateQueries({ queryKey: ['support-ticket', selectedTicketId] })
      setReplyMessage('')
      setSuccess('Reply sent successfully!')
      setTimeout(() => setSuccess(''), 3000)
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to send reply')
    },
  })

  const resolveMutation = useMutation({
    mutationFn: (id: number) => supportApi.resolve(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['support-tickets'] })
      queryClient.invalidateQueries({ queryKey: ['support-ticket', selectedTicketId] })
      setSuccess('Ticket resolved successfully!')
      setTimeout(() => setSuccess(''), 3000)
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to resolve ticket')
    },
  })

  const tickets = data?.data?.tickets || []
  const selectedTicket = ticketData?.data?.ticket

  const handleCreateSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!formData.category || !formData.message.trim()) {
      setError('Please fill in all required fields')
      return
    }

    createMutation.mutate(formData)
  }

  const handleReplySubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedTicketId || !replyMessage.trim()) return

    replyMutation.mutate({ id: selectedTicketId, message: replyMessage })
  }

  if (isLoading) return <Loading />

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Support Tickets</h1>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="w-4 h-4 mr-2" />
          New Ticket
        </Button>
      </div>

      {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}
      {error && <Alert type="error" message={error} onClose={() => setError('')} />}

      <PageNotification pageRoute="support" />

      {/* Tickets Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <ResponsiveTable>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Ticket #
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Subject / Category
              </th>
              {isAdmin && (
                <th className="hide-mobile px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-priority="3">
                  User
                </th>
              )}
              <th className="hide-mobile px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-priority="2">
                Status
              </th>
              <th className="hide-mobile px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-priority="1">
                Messages
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Updated
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {tickets.map((ticket: SupportTicket) => (
              <tr
                key={ticket.id}
                className="hover:bg-gray-50 cursor-pointer"
                onClick={() => setSelectedTicketId(ticket.id)}
              >
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  #{ticket.id}
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">
                  <div className="font-medium">{ticket.details?.substring(0, 50) || 'No subject'}{ticket.details && ticket.details.length > 50 ? '...' : ''}</div>
                  <div className="text-gray-500 text-xs capitalize">{ticket.reason}</div>
                </td>
                {isAdmin && (
                  <td className="hide-mobile px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-priority="3" data-label="User">
                    {ticket.first_name} {ticket.last_name}
                  </td>
                )}
                <td className="hide-mobile px-6 py-4 whitespace-nowrap" data-priority="2" data-label="Status">
                  <Badge status={ticket.status}>{ticket.status}</Badge>
                </td>
                <td className="hide-mobile px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-priority="1" data-label="Messages">
                  <div className="flex items-center">
                    <MessageCircle className="w-4 h-4 mr-1" />
                    {ticket.message_count || 0}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {formatDate(ticket.updated_at)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button
                    className="mobile-details-trigger text-blue-600 hover:text-blue-900 mr-2"
                    title="View Hidden Details"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Info className="w-4 h-4 inline" />
                  </button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={(e) => {
                      e.stopPropagation()
                      setSelectedTicketId(ticket.id)
                    }}
                  >
                    View
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
          </table>
        </ResponsiveTable>

        {tickets.length === 0 && (
          <div className="text-center py-12">
            <MessageCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 mb-4">No support tickets yet</p>
            <Button onClick={() => setShowCreateModal(true)}>Create Your First Ticket</Button>
          </div>
        )}
      </div>

      {/* Create Ticket Modal */}
      {showCreateModal && (
        <Modal title="Create Support Ticket" onClose={() => setShowCreateModal(false)}>
          <form onSubmit={handleCreateSubmit} className="space-y-4">
            <div>
              <label className="label">Category *</label>
              <select
                className="select"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                required
              >
                <option value="general">General</option>
                <option value="issue">Issue</option>
                <option value="payout">Payout</option>
                <option value="barter mitigation">Barter Mitigation</option>
                <option value="billing">Billing</option>
                <option value="support">Support</option>
              </select>
            </div>

            <div>
              <label className="label">Subject</label>
              <input
                type="text"
                className="input"
                placeholder="Brief description of your issue (optional)"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              />
            </div>

            <div>
              <label className="label">Message *</label>
              <textarea
                className="textarea"
                rows={6}
                placeholder="Provide detailed information about your issue..."
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                required
              />
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button type="submit" isLoading={createMutation.isPending}>
                Submit Ticket
              </Button>
            </div>
          </form>
        </Modal>
      )}

      {/* Ticket Detail Modal */}
      {selectedTicketId && (
        <Modal
          title={`Ticket #${selectedTicketId}`}
          onClose={() => {
            setSelectedTicketId(null)
            setReplyMessage('')
          }}
          size="xl"
        >
          {isLoadingTicket ? (
            <Loading />
          ) : !selectedTicket ? (
            <div className="text-center py-12">
              <p className="text-gray-500">Unable to load ticket details</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Ticket Header */}
              <div className="border-b pb-4">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h3 className="text-lg font-semibold">
                      Ticket #{selectedTicket.id}
                    </h3>
                    <p className="text-sm text-gray-500">
                      <span className="capitalize">Category: {selectedTicket.reason}</span>
                      {isAdmin && selectedTicket.first_name && (
                        <> • User: {selectedTicket.first_name} {selectedTicket.last_name}</>
                      )}
                    </p>
                  </div>
                  <Badge status={selectedTicket.status}>{selectedTicket.status}</Badge>
                </div>
                <div className="text-xs text-gray-500">
                  Created: {formatDate(selectedTicket.created_at)} • Updated:{' '}
                  {formatDate(selectedTicket.updated_at)}
                </div>
              </div>

              {/* Message Thread */}
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {selectedTicket.messages && selectedTicket.messages.length > 0 ? (
                  selectedTicket.messages.map((msg) => {
                    const isAdminMsg = msg.user_type === 'super_admin' || msg.user_type === 'admin'
                    return (
                      <div
                        key={msg.id}
                        className={`p-4 rounded-lg ${
                          isAdminMsg
                            ? 'bg-primary-50 border-l-4 border-primary-600'
                            : 'bg-gray-50'
                        }`}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div className="font-medium text-sm">
                            {msg.user_id === user?.id ? (
                              <span>You</span>
                            ) : (
                              <button
                                onClick={() => navigate(`/dashboard/users/${msg.user_id}`)}
                                className="hover:underline text-primary-600"
                              >
                                {msg.first_name} {msg.last_name}
                              </button>
                            )}
                            {isAdminMsg && (
                              <span className="ml-2 text-xs bg-primary-600 text-white px-2 py-0.5 rounded">
                                Admin
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500">{formatDate(msg.created_at)}</div>
                        </div>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{msg.message}</p>
                      </div>
                    )
                  })
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <MessageCircle className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                    <p>No messages yet</p>
                  </div>
                )}
              </div>

              {/* Image Attachments */}
              <div className="border-t pt-4">
                <SupportTicketImageUpload ticketId={selectedTicketId} />
              </div>

              {/* Reply Form */}
              {(selectedTicket.status === 'open' || selectedTicket.status === 'in_progress') && (
                <form onSubmit={handleReplySubmit} className="space-y-4 border-t pt-4">
                  <div>
                    <label className="label">Your Reply *</label>
                    <textarea
                      className="textarea"
                      rows={4}
                      placeholder="Type your reply..."
                      value={replyMessage}
                      onChange={(e) => setReplyMessage(e.target.value)}
                      required
                    />
                  </div>

                  <div className="flex justify-between">
                    <div>
                      {isAdmin && (
                        <Button
                          variant="success"
                          onClick={() => resolveMutation.mutate(selectedTicketId)}
                          isLoading={resolveMutation.isPending}
                        >
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Mark as Resolved
                        </Button>
                      )}
                    </div>
                    <Button type="submit" isLoading={replyMutation.isPending}>
                      <MessageCircle className="w-4 h-4 mr-2" />
                      Send Reply
                    </Button>
                  </div>
                </form>
              )}

              {selectedTicket.status === 'resolved' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <p className="text-green-700 font-medium">This ticket has been resolved</p>
                </div>
              )}
            </div>
          )}
        </Modal>
      )}
    </div>
  )
}
